import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, FileSignature, CreditCard, Loader2, AlertCircle, ExternalLink,
} from 'lucide-react';
import { proposalsApi } from '../utils/api';

// Maps the current_stage returned by the backend to the page's view state.
// We render one of: contract | onboarding | active | redirect
function stageView(stage) {
  if (stage === 'contract') return 'contract';
  if (stage === 'onboarding') return 'onboarding';
  if (stage === 'active' || stage === 'completed') return 'active';
  // lead / discovery / proposal / declined => bounce back to proposal page
  return 'redirect';
}

export default function ContractSigning() {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // sign_url passed via navigate state from Proposal.jsx accept handler.
  // It may be undefined if the accept response didn't include one (e.g.
  // signing-package creation deferred / failed inline).
  const signUrlFromState = location.state?.signUrl || null;
  const packageTokenFromState = location.state?.packageToken || null;

  const [status, setStatus] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [pkg, setPkg] = useState(null);   // signing package fetched on load
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Payment-method setup
  const [pmLoading, setPmLoading] = useState(false);
  const [pmError, setPmError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch both in parallel — proposal gives us customer_name + lifecycle
      // for header rendering, status drives branching.
      const [s, p, pk] = await Promise.all([
        proposalsApi.getOnboardingStatus(pathId).catch((e) => {
          // If Agent 2's endpoint isn't deployed yet, fall back to deriving
          // a minimal status from the proposal payload.
          if (String(e.message || '').includes('404') || String(e.message || '').toLowerCase().includes('not found')) {
            return null;
          }
          throw e;
        }),
        proposalsApi.getProposal(pathId).catch(() => null),
        // Fetch the signing package so a refresh (which loses location.state)
        // still shows "Review & Sign". At the contract stage the backend
        // lazily (re)generates the package if none exists, so this is also
        // what unsticks a client whose package was cleared. 404 => null.
        proposalsApi.getSigningPackage(pathId).catch(() => null),
      ]);
      setProposal(p);
      setPkg(pk);
      // Defensive fallback if onboarding-status isn't ready: synthesize from proposal
      if (s) {
        setStatus(s);
      } else if (p) {
        setStatus({
          current_stage: p.current_stage,
          require_autopay: !!p.proposal?.require_autopay,
          has_payment_method: false,
          ready_for_kickoff: false,
          payment_method_summary: null,
        });
      } else {
        throw new Error('Unable to load contract details.');
      }
    } catch (e) {
      setError(e.message || 'Failed to load contract details.');
    } finally {
      setLoading(false);
    }
  }, [pathId]);

  useEffect(() => { load(); }, [load]);

  // Bounce back to the proposal page if the stage isn't post-accept.
  useEffect(() => {
    if (!loading && status && stageView(status.current_stage) === 'redirect') {
      navigate(`/proposal/${pathId}`, { replace: true });
    }
  }, [loading, status, navigate, pathId]);

  const handleStartSigning = async () => {
    // Prefer the URL/token we already have. Otherwise look it up.
    if (signUrlFromState) {
      navigate(signUrlFromState);
      return;
    }
    if (packageTokenFromState) {
      navigate(`/sign/${packageTokenFromState}`);
      return;
    }
    // Use the package fetched on load (also covers refresh + lazy-regen).
    if (pkg?.sign_url) { navigate(pkg.sign_url); return; }
    if (pkg?.package_token) { navigate(`/sign/${pkg.package_token}`); return; }
    // Fallback: look up the active signing package for this path.
    try {
      const pkg = await proposalsApi.getSigningPackage(pathId);
      if (pkg?.sign_url) {
        navigate(pkg.sign_url);
      } else if (pkg?.package_token) {
        navigate(`/sign/${pkg.package_token}`);
      } else {
        alert("Couldn't find your signing package. Please use the link from your email or contact support.");
      }
    } catch (e) {
      alert("Couldn't find your signing package. Please use the link from your email or contact support.");
    }
  };

  const handleSetupPayment = async () => {
    setPmLoading(true);
    setPmError('');
    try {
      const res = await proposalsApi.setupPaymentMethod(pathId);
      if (!res?.url) throw new Error('Stripe setup URL was missing from the response.');
      window.location.href = res.url;
    } catch (e) {
      setPmError(e.message || 'Failed to start payment setup.');
      setPmLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Loader2 className="spinner-large" size={48} />
        <p>Loading contract details…</p>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="proposal-page">
        <div className="proposal-topbar">
          <button onClick={() => navigate('/')} className="back-nav-button">
            <ArrowLeft size={16} /> Dashboard
          </button>
        </div>
        <div className="error-message">{error || 'Contract not found.'}</div>
      </div>
    );
  }

  const view = stageView(status.current_stage);
  const customerName = proposal?.customer_name || 'your account';

  return (
    <div className="proposal-page contract-page">
      <div className="proposal-topbar">
        <button onClick={() => navigate('/')} className="back-nav-button">
          <ArrowLeft size={16} /> Dashboard
        </button>
      </div>

      <header className="proposal-header">
        <div>
          <h1>Welcome, {customerName}</h1>
          <div className="proposal-header-meta">Let's finish setting up your account.</div>
        </div>
        <span
          className="proposal-status-pill"
          style={{ color: '#16a34a', background: '#dcfce7' }}
        >
          Accepted
        </span>
      </header>

      {/* Progress strip */}
      <ContractProgress view={view} requireAutopay={!!status.require_autopay} hasPaymentMethod={!!status.has_payment_method} />

      {view === 'contract' && (
        <ContractStep
          signUrl={signUrlFromState || pkg?.sign_url}
          packageToken={packageTokenFromState || pkg?.package_token}
          onStart={handleStartSigning}
        />
      )}

      {view === 'onboarding' && (
        <OnboardingStep
          requireAutopay={!!status.require_autopay}
          hasPaymentMethod={!!status.has_payment_method}
          paymentMethodSummary={status.payment_method_summary}
          pmLoading={pmLoading}
          pmError={pmError}
          onSetupPayment={handleSetupPayment}
        />
      )}

      {view === 'active' && <ActiveStep />}
    </div>
  );
}

// -------------------- Sub-components --------------------

function ContractProgress({ view, requireAutopay, hasPaymentMethod }) {
  const steps = [
    { key: 'contract', label: 'Sign agreement', done: view !== 'contract' },
    requireAutopay
      ? {
          key: 'payment',
          label: 'Add payment method',
          done: hasPaymentMethod || view === 'active',
        }
      : null,
    { key: 'onboarding', label: 'Onboarding', done: view === 'active' },
  ].filter(Boolean);

  return (
    <div className="contract-page-progress">
      {steps.map((s, idx) => (
        <React.Fragment key={s.key}>
          <div className={`contract-page-progress-step ${s.done ? 'done' : ''}`}>
            <div className="contract-page-progress-dot">
              {s.done ? <CheckCircle size={14} /> : <span>{idx + 1}</span>}
            </div>
            <span className="contract-page-progress-label">{s.label}</span>
          </div>
          {idx < steps.length - 1 && <div className={`contract-page-progress-line ${s.done ? 'done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function ContractStep({ signUrl, packageToken, onStart }) {
  const haveSigningTarget = !!(signUrl || packageToken);
  return (
    <section className="contract-page-card">
      <div className="contract-page-card-icon">
        <FileSignature size={24} />
      </div>
      <div className="contract-page-card-body">
        <h2>Sign your Terms of Service</h2>
        <p>
          We've prepared the contract documents based on your accepted proposal. Review and
          sign them to formalize the engagement and unlock the next step.
        </p>
        {haveSigningTarget ? (
          <button
            type="button"
            className="proposal-action-btn proposal-accept-btn"
            onClick={onStart}
          >
            <FileSignature size={16} /> Review &amp; Sign
          </button>
        ) : (
          <div className="contract-page-pending-note">
            <Loader2 className="spinner" size={16} />
            <span>
              Loading signing details… If this persists, please use the link emailed to you,
              or contact your account team.
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function OnboardingStep({
  requireAutopay,
  hasPaymentMethod,
  paymentMethodSummary,
  pmLoading,
  pmError,
  onSetupPayment,
}) {
  // Auto-pay required AND no payment method on file => prompt to add one.
  if (requireAutopay && !hasPaymentMethod) {
    return (
      <section className="contract-page-card">
        <div className="contract-page-card-icon">
          <CreditCard size={24} />
        </div>
        <div className="contract-page-card-body">
          <h2>Set up auto-pay</h2>
          <p>
            Your agreement requires a payment method on file for monthly auto-pay. Add a card
            or bank account through our secure payment processor (Stripe) to continue.
          </p>
          {pmError && (
            <div className="proposal-banner proposal-banner-error">
              <AlertCircle size={18} />
              <span>{pmError}</span>
            </div>
          )}
          <button
            type="button"
            className="proposal-action-btn proposal-accept-btn"
            onClick={onSetupPayment}
            disabled={pmLoading}
          >
            {pmLoading ? <Loader2 className="spinner" size={16} /> : <CreditCard size={16} />}
            {' '}Add Payment Method
            {!pmLoading && <ExternalLink size={14} style={{ marginLeft: 4, opacity: 0.8 }} />}
          </button>
          <p className="contract-page-fineprint">
            You'll be redirected to Stripe to enter your details. We never see your card or
            bank info directly.
          </p>
        </div>
      </section>
    );
  }

  // Either auto-pay isn't required, or it's already covered.
  return (
    <section className="contract-page-card contract-page-card-success">
      <div className="contract-page-card-icon contract-page-card-icon-success">
        <CheckCircle size={24} />
      </div>
      <div className="contract-page-card-body">
        <h2>You're all set</h2>
        <p>
          Thanks — your account team will reach out shortly to start onboarding. You'll get
          updates here as things move forward.
        </p>
        {paymentMethodSummary && (
          <div className="contract-page-pm-summary">
            <CreditCard size={14} />
            <span>
              Auto-pay is set up with{' '}
              <strong>
                {paymentMethodSummary.brand}
                {paymentMethodSummary.last4 ? ` •••• ${paymentMethodSummary.last4}` : ''}
              </strong>
              {paymentMethodSummary.type ? ` (${paymentMethodSummary.type})` : ''}.
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function ActiveStep() {
  return (
    <section className="contract-page-card contract-page-card-success">
      <div className="contract-page-card-icon contract-page-card-icon-success">
        <CheckCircle size={24} />
      </div>
      <div className="contract-page-card-body">
        <h2>Welcome — you're all set up</h2>
        <p>
          Your account is active. You can manage invoices and payment methods from your
          billing dashboard.
        </p>
        <Link to="/billing" className="proposal-action-btn proposal-question-btn">
          Go to Billing
        </Link>
      </div>
    </section>
  );
}
